import type { RouteObject } from "react-router-dom";
import { addSlashes } from "../route.utils";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  LeaveManagement,
  MsLeaveManagementLayout,
  NewLeaveRequest,
} from "@/utils/lazyLoadComponents";
import { store } from "@/store";
import { leaveRequestApi } from "@/services/(my-space)/api.leaves";

// ─── Path Segments ────────────────────────────────────────────────────────────
export const MS_LEAVES_MANAGEMENT_DYNAMIC_ID_KEY = "id";

export enum MS_LEAVES_MANAGEMENT_PATH_SEGMENTS {
  ROOT = "leave-management",
  DETAILS = `:${MS_LEAVES_MANAGEMENT_DYNAMIC_ID_KEY}`,
  APPLY = "apply",
  EDIT = "edit",
}

// ─── Route Definition ─────────────────────────────────────────────────────────
export const myspaceLeavesManagementRoutes: RouteObject = {
  path: MS_LEAVES_MANAGEMENT_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Leave Management" },
  HydrateFallback: Spinner,
  children: [
    // ── Page-level tabs layout (single "My Leaves" tab at the module root) ───
    {
      Component: MsLeaveManagementLayout,
      handle: { flushContent: true },
      children: [{ index: true, Component: LeaveManagement }],
    },
    {
      path: MS_LEAVES_MANAGEMENT_PATH_SEGMENTS.APPLY,
      handle: { breadcrumb: "Apply Leave" },
      Component: NewLeaveRequest,
    },
    {
      path: MS_LEAVES_MANAGEMENT_PATH_SEGMENTS.DETAILS,
      loader: async ({ params }) => {
        const leaveId = params[MS_LEAVES_MANAGEMENT_DYNAMIC_ID_KEY]!;
        const result = await store.dispatch(leaveRequestApi.endpoints.msGetLeaveRequestById.initiate({ leaveId }));
        const name = result.data?.data?.leaveRequest?.leaveType?.name ?? `Leave #${leaveId}`;
        return { breadcrumbLabel: name };
      },
      handle: {
        breadcrumb: (data: { breadcrumbLabel: string }) => data?.breadcrumbLabel ?? "Leave Details",
      },
      children: [
        {
          index: true,
          Component: NewLeaveRequest,
        },
        {
          path: MS_LEAVES_MANAGEMENT_PATH_SEGMENTS.EDIT,
          handle: { breadcrumb: "Edit" },
          Component: NewLeaveRequest,
        },
      ],
    },
  ],
};

// ─── Resolved Paths ───────────────────────────────────────────────────────────
export const MS_LEAVES_MANAGEMENT_PATHS = Object.freeze({
  ROOT: addSlashes(MS_LEAVES_MANAGEMENT_PATH_SEGMENTS.ROOT),
  APPLY: addSlashes(MS_LEAVES_MANAGEMENT_PATH_SEGMENTS.ROOT, MS_LEAVES_MANAGEMENT_PATH_SEGMENTS.APPLY),
  VIEW: (id: string | number) => addSlashes(MS_LEAVES_MANAGEMENT_PATH_SEGMENTS.ROOT, String(id)),
  EDIT: (id: string | number) =>
    addSlashes(MS_LEAVES_MANAGEMENT_PATH_SEGMENTS.ROOT, String(id), MS_LEAVES_MANAGEMENT_PATH_SEGMENTS.EDIT),
});
